import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, deleteRequest, patchRequest } from "../http/api";
import { useConfirmModal } from "../components/reusable/ConfirmModalContext";
import { useTranslation } from "react-i18next";


import {
    FaBriefcase,
    FaUsers,
    FaArrowRight,
    FaTrash,
    FaLock,
    FaSearch,
    FaBolt
}
    from "react-icons/fa";

import "./ClubOffers.css";

export default function ClubOffers() {

    const navigate =
        useNavigate();

    const [
        offers,
        setOffers
    ] = useState([]);

    const [
        loading,
        setLoading
    ] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");


    useEffect(() => {

        fetchOffers();

    }, []);

    const {
        openConfirmModal,
        closeModal
    } = useConfirmModal();

    const user = JSON.parse(
        sessionStorage.getItem(
            "user"
        )
    );

    const { t } = useTranslation();

    const filteredOffers = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase();
        if (!query) return offers;

        return offers.filter((offer) =>
            [
                offer.title,
                offer.status,
                offer.description,
                offer.country,
                offer.city
            ]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase().includes(query))
        );
    }, [offers, searchQuery]);

    const activeOffers = offers.filter((offer) => offer.status === "active").length;
    const totalApplications = offers.reduce(
        (total, offer) => total + Number(offer.applications_count || 0),
        0
    );

    const navigateWithTransition = (target) => {
        if (document.startViewTransition) {
            document.startViewTransition(() => navigate(target));
        } else {
            navigate(target);
        }
    };


    const fetchOffers =
        async () => {

            try {

                const res =
                    await get(
                        "offers/my"
                    );

                setOffers(
                    res.data || []
                );

            }
            catch (err) {

                console.log(err);

            }
            finally {

                setLoading(
                    false
                );

            }

        };


    const updateOfferStatus =
        async (

            id,
            status

        ) => {

            try {

                await patchRequest(

                    `offers/${id}/status`,

                    {

                        status

                    }

                );


                setOffers(

                    prev =>

                        prev.map(

                            offer =>

                                offer.id_offer === id

                                    ?

                                    {

                                        ...offer,

                                        status

                                    }

                                    :

                                    offer

                        )

                );

            }
            catch (err) {

                console.log(
                    err
                );

            }

        };


    const deleteOffer = (id) => {
        openConfirmModal({

            title: t("clubOffers.confirmDelete.title"),
            message: t("clubOffers.confirmDelete.message"),
            confirmText: t("clubOffers.confirmDelete.confirm"),
            variant: "danger",
            cancelText: t("clubOffers.confirmDelete.cancel"),

            onConfirm: async () => {
                try {
                    await deleteRequest(
                        `offers/${id}`
                    );
                    setOffers(

                        prev =>
                            prev.filter(
                                o =>
                                    o.id_offer !== id
                            )
                    );
                    closeModal();
                }
                catch (err) {

                    console.error(err);

                }

            }

        });
    }


    if (
        user?.role !== "club"
    ) {

        return (

            <div
                className="access-denied-page"
            >

                <div
                    className="access-denied-card"
                >

                    <div
                        className="access-icon"
                    >

                        <FaLock />

                    </div>

                    <h2>
                        {t("clubOffers.accessDenied.title")}
                    </h2>

                    <p>
                        {t("clubOffers.accessDenied.message")}
                    </p>

                    <button
                        className="back-home-btn"

                        onClick={() => {

                            navigate(
                                "/home"
                            );

                        }}

                    >
                        {t("clubOffers.accessDenied.back")}
                    </button>

                </div>

            </div>

        );

    }


    return (

        <div className="club-offers-page">

            <button
                className="sport-back-btn"
                onClick={() => navigate(-1)}
                style={{ marginBottom: "20px" }}
            >
                <span className="back-arrow">←</span>

                <div className="back-content">
                    <span className="back-label">
                        {t("clubOffers.back.label")}
                    </span>

                    <small>
                        {t("clubOffers.back.subtitle")}
                    </small>
                </div>
            </button>

            <div className="club-offers-header">

                <div>
                    <span className="club-offers-kicker"><FaBolt /> {t("clubOffers.header.kicker")}</span>

                    <h1>
                        {t("clubOffers.header.title")}
                    </h1>

                    <p>
                        {t("clubOffers.header.description")}
                    </p>

                </div>

                <div className="club-offers-icon">

                    <FaBriefcase />

                </div>

            </div>

            <div className="club-offers-commandbar">
                <label className="club-offers-search">
                    <FaSearch aria-hidden="true" />
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder={t("clubOffers.search.placeholder")}
                        aria-label={t("clubOffers.search.placeholder")}
                    />
                    {searchQuery && (
                        <button type="button" onClick={() => setSearchQuery("")} aria-label={t("clubOffers.search.clear")}>
                            ×
                        </button>
                    )}
                </label>

                <div className="club-offers-stats">
                    <div><strong>{offers.length}</strong><span>{t("clubOffers.metrics.total")}</span></div>
                    <div><strong>{activeOffers}</strong><span>{t("clubOffers.metrics.active")}</span></div>
                    <div><strong>{totalApplications}</strong><span>{t("clubOffers.metrics.applications")}</span></div>
                </div>
            </div>


            <div className="club-offers-grid">

                {

                    loading ?

                        Array.from(
                            { length: 6 }
                        ).map((_, i) => (

                            <div
                                key={i}
                                className="offer-skeleton"
                            />

                        ))

                        :

                        filteredOffers.length === 0 ?

                            <div
                                className="empty-offers"
                            >

                                <h3>
                                    {searchQuery ? t("clubOffers.search.emptyTitle") : t("clubOffers.empty.title")}
                                </h3>

                                <p>
                                    {searchQuery ? t("clubOffers.search.emptyDescription") : t("clubOffers.empty.description")}
                                </p>

                            </div>

                            :

                            filteredOffers.map(

                                offer => (

                                    <div

                                        key={
                                            offer.id_offer
                                        }

                                        className="club-offer-card"

                                        onClick={() => {

                                            navigateWithTransition(`/offers/${offer.id_offer}/applications`);

                                        }}

                                    >

                                        <div className="club-offer-top">

                                            <div
                                                className="club-offer-badge"
                                            >

                                                ⚽

                                            </div>

                                            <div
                                                className="club-offer-header-content"
                                            >

                                                <div
                                                    className={`offer-status ${offer.status}`}
                                                >

                                                    {

                                                        offer.status === "active"
                                                            ? t("clubOffers.status.active")
                                                            : t("clubOffers.status.closed")

                                                    }

                                                </div>

                                                <h3>

                                                    {
                                                        offer.title
                                                    }

                                                </h3>


                                            </div>

                                        </div>


                                        <div>
                                            <FaTrash
                                                className="offer-delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteOffer(offer.id_offer)
                                                }}
                                            />
                                        </div>



                                        <div
                                            className="club-offer-bottom"
                                        >

                                            <div>

                                                <FaUsers />

                                                <span>
                                                    {
                                                        offer.applications_count
                                                    } &nbsp;

                                                    {t("clubOffers.applications")}
                                                </span>

                                            </div>


                                            <div
                                                className="offer-actions-row"
                                            >

                                                <button

                                                    className={

                                                        offer.status === "active"

                                                            ?

                                                            "finish-btn"

                                                            :

                                                            "reopen-btn"

                                                    }

                                                    onClick={(e) => {

                                                        e.stopPropagation();

                                                        updateOfferStatus(

                                                            offer.id_offer,

                                                            offer.status === "active"

                                                                ?

                                                                "closed"

                                                                :

                                                                "active"

                                                        );

                                                    }}

                                                >

                                                    {
                                                        offer.status === "active"
                                                            ? t("clubOffers.actions.finish")
                                                            : t("clubOffers.actions.reopen")
                                                    }

                                                </button>

                                                <div>
                                                    {t("clubOffers.actions.view")}
                                                    <FaArrowRight />
                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                )
                            )
                }
            </div>
        </div>
    );
}
